import React, { useCallback, useEffect, useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { useLazyGetAccountQuery, useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { Controller, useForm } from 'react-hook-form'
import { FormFeedback } from '../../../../components/Form/FormFeedback'
import { Button } from '../../../../components/Button/Button'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { Account } from '../../../../models/Account'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { LogoSettings } from './Logo'

interface FormValues {
  accountName: string
  crossAccountAccess: boolean
  inviteLinkAutoRenewalEnabled: boolean
}

export const AccountSettings: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const [triggerGetAccount] = useLazyGetAccountQuery()
  const [updateAccount, { isLoading: accountUpdateLoading }] = useUpdateAccountMutation()
  const [account, setAccount] = useState<Account | null>(null)
  const [accountLoading, setAccountLoading] = useState<boolean>(true)
  const { hasQuota } = useZocksQuota()
  const crossAccountLoginQuota = hasQuota('auth.crossAccountLoginEnabled.value')
  const inviteLinkAutoRenewalQuota = hasQuota('auth.inviteLinkAutoRenewal.value')
  const [crossAccountAccessIsManagedAtTheEnterpriseLevel, setCrossAccountAccessIsManagedAtTheEnterpriseLevel] = useState<boolean>(false)

  const getAccount = async (): Promise<FormValues> => {
    const result = await triggerGetAccount({ accountId })
    setAccountLoading(true)
    try {
      if (result.data) {
        setAccount(result.data.data)
      }
    } finally {
      setAccountLoading(false)
    }
    return result.data
      ? {
          accountName: result.data.data.name,
          crossAccountAccess: result.data.data.effectiveConfig.crossAccountAccess,
          inviteLinkAutoRenewalEnabled: result.data.data.config.inviteLinkAutoRenewal,
        }
      : {
          accountName: '',
          crossAccountAccess: false,
          inviteLinkAutoRenewalEnabled: true,
        }
  }

  useEffect(() => {
    if (account) {
      setCrossAccountAccessIsManagedAtTheEnterpriseLevel(account.enterpriseConfig.crossAccountAccess != null)
    }
  }, [account])

  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
    resetField,
    reset,
  } = useForm<FormValues>({ defaultValues: getAccount })

  const isAccountNameDirty = dirtyFields.accountName

  const onCancelAccountNameChange = () => {
    resetField('accountName')
  }

  const onSubmitAccountNameChange = async (data: FormValues) => {
    await updateAccount({
      accountId,
      payload: {
        name: data.accountName,
      },
    })
    reset(await getAccount())
  }

  const onSubmitCrossAccountAccessChange = async (data: FormValues) => {
    if (!account) {
      return
    }
    const accountConfig = { ...account.config, crossAccountAccess: data.crossAccountAccess }
    await updateAccount({
      accountId,
      payload: {
        config: accountConfig,
      },
    })
    reset(await getAccount())
  }

  const onSubmitInviteLinkAutoRenewalChange = async (data: FormValues) => {
    if (!account) {
      return
    }
    const accountConfig = { ...account.config, inviteLinkAutoRenewal: data.inviteLinkAutoRenewalEnabled }
    await updateAccount({
      accountId,
      payload: {
        config: accountConfig,
      },
    })
    reset(await getAccount())
  }

  return (
    <>
      <Container fluid className='p-0'>
        <Row className='zocks-mb-2'>
          <Col>
            <h4 className='zocks-text-gray-900'>Account Name</h4>
          </Col>
        </Row>
        <Row className='mb-1'>
          <div className='paragraph-medium'>Account name</div>
        </Row>
        <Row>
          <Col className='pe-2'>
            <Controller
              render={({ field, fieldState }) => {
                return (
                  <>
                    <ScopedBlock
                      scope={{
                        scope: 'account:update',
                        superScope: ['account:admin'],
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <Form.Control
                        id={field.name}
                        type='text'
                        {...field}
                        placeholder='Account name'
                        isInvalid={!!fieldState.error}
                        disabled={accountLoading || accountUpdateLoading}
                        maxLength={128}
                      />
                      {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                    </ScopedBlock>
                  </>
                )
              }}
              name='accountName'
              rules={{
                required: 'Account name is required.',
              }}
              control={control}
            />
          </Col>
          <Col xs='auto' className='ps-2'>
            <div className='d-flex gap-2'>
              <ScopedBlock
                scope={{
                  scope: 'account:update',
                  superScope: ['account:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <Button variant='secondary' disabled={accountLoading || accountUpdateLoading || !isAccountNameDirty} onClick={onCancelAccountNameChange}>
                  Cancel
                </Button>
                <Button
                  variant='primary'
                  disabled={accountLoading || accountUpdateLoading || !isAccountNameDirty}
                  onClick={handleSubmit(onSubmitAccountNameChange)}
                >
                  Save
                </Button>
              </ScopedBlock>
            </div>
          </Col>
        </Row>
        <Row className='zocks-mt-4.5'>
          <LogoSettings />
        </Row>
        <Row className='zocks-mt-4.5'>
          <Col>
            <h4 className='zocks-text-gray-900'>Allow cross-account access for external users</h4>
          </Col>
        </Row>
        <Container className='my-4'>
          <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  You can grant cross-account access to members of other Zocks accounts, allowing them to join your team.
                </span>
              </div>
              {!crossAccountLoginQuota && (
                <ScopedBlock
                  scope={{
                    scope: 'account:update',
                    superScope: ['account:admin'],
                  }}
                  quota={{
                    quota: 'auth.crossAccountLoginEnabled.value',
                  }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                >
                  <Button variant={'primary'}>Upgrade</Button>
                </ScopedBlock>
              )}
            </Col>
          </Row>
        </Container>
        {crossAccountAccessIsManagedAtTheEnterpriseLevel ? (
          <Container className='zocks-mt-2.0 zocks-mb-2'>
            <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
              <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
                <div className='d-flex align-self-center gap-3 align-items-center'>
                  <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                  <span className='zocks-text-gray-900'>Cross-account access is managed at the enterprise level.</span>
                </div>
              </Col>
            </Row>
          </Container>
        ) : null}
        <Row>
          <Col>
            <div className='paragraph-medium'>Enable cross-account access</div>
          </Col>
          <Col>
            <Controller
              name='crossAccountAccess'
              control={control}
              render={({ field }) => {
                return (
                  <ScopedBlock
                    scope={{
                      scope: 'account:update',
                      superScope: ['account:admin'],
                    }}
                    quota={{
                      quota: 'auth.crossAccountLoginEnabled.value',
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Form.Switch
                      disabled={crossAccountAccessIsManagedAtTheEnterpriseLevel || accountLoading || accountUpdateLoading}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e)
                        handleSubmit(onSubmitCrossAccountAccessChange)()
                      }}
                    />
                  </ScopedBlock>
                )
              }}
            />
          </Col>
        </Row>
        <Row className='zocks-mt-2.0'>
          <Col>
            <h4 className='zocks-text-gray-900'>Auto-renew expired invitations</h4>
          </Col>
        </Row>
        <Container className='my-4'>
          <Row className={`${inviteLinkAutoRenewalQuota ? 'p-4' : 'p-3'} zocks-bg-color-primary-light gap-3 justify-content-center rounded`}>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>Automatically renew expired invitation links when users attempt to access them.</span>
              </div>
              {!inviteLinkAutoRenewalQuota && (
                <ScopedBlock
                  scope={{
                    scope: 'account:update',
                    superScope: ['account:admin'],
                  }}
                  quota={{
                    quota: 'auth.inviteLinkAutoRenewal.value',
                  }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                >
                  <Button variant={'primary'}>Upgrade</Button>
                </ScopedBlock>
              )}
            </Col>
          </Row>
          <Row className='gap-3 p-0 align-items-center'>
            <Col className='p-0'>
              <div className='paragraph-medium'>Enable link auto-renewal</div>
            </Col>
            <Col className='p-4'>
              <Controller
                name='inviteLinkAutoRenewalEnabled'
                control={control}
                render={({ field }) => {
                  return (
                    <ScopedBlock
                      scope={{
                        scope: 'account:update',
                        superScope: ['account:admin'],
                      }}
                      quota={{
                        quota: 'auth.inviteLinkAutoRenewal.value',
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <Form.Switch
                        disabled={accountLoading || accountUpdateLoading}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e)
                          handleSubmit(onSubmitInviteLinkAutoRenewalChange)()
                        }}
                      />
                    </ScopedBlock>
                  )
                }}
              />
            </Col>
          </Row>
        </Container>
      </Container>
    </>
  )
}
